import React, { useState, useEffect } from 'react';
import EditModalWrapper from './EditModalWrapper';

const LANGUAGES = [
    'German', 'English', 'French', 'Spanish', 'Italian', 'Portuguese',
    'Dutch', 'Russian', 'Arabic', 'Chinese', 'Japanese', 'Korean',
    'Hindi', 'Malayalam', 'Tamil', 'Telugu', 'Kannada', 'Bengali',
    'Marathi', 'Gujarati', 'Punjabi', 'Urdu', 'Turkish', 'Polish',
    'Swedish', 'Norwegian', 'Danish', 'Finnish', 'Greek', 'Czech'
];

const PROFICIENCY_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Native'];

const EditLanguageModal = ({
    isOpen,
    onClose,
    languageData = null, // null for new, object for editing
    onSave,
    isAddMode = true // true for adding, false for editing
}) => {
    const [form, setForm] = useState({
        language: '',
        listeningLevel: '',
        listeningCertifiedSince: '',
        readingLevel: '',
        readingCertifiedSince: '',
        speakingLevel: '',
        speakingCertifiedSince: '',
        writingLevel: '',
        writingCertifiedSince: '',
    });

    useEffect(() => {
        if (isOpen) {
            setForm({
                language: languageData?.language || '',
                listeningLevel: languageData?.listening_level || languageData?.listening || '',
                listeningCertifiedSince: (languageData?.listening_certified_since || '').split('T')[0],
                readingLevel: languageData?.reading_level || languageData?.reading || '',
                readingCertifiedSince: (languageData?.reading_certified_since || '').split('T')[0],
                speakingLevel: languageData?.speaking_level || languageData?.speaking || '',
                speakingCertifiedSince: (languageData?.speaking_certified_since || '').split('T')[0],
                writingLevel: languageData?.writing_level || languageData?.writing || '',
                writingCertifiedSince: (languageData?.writing_certified_since || '').split('T')[0],
            });
        }
    }, [isOpen, languageData]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.language) {
            alert('Language is required');
            return;
        }
        if (!form.listeningLevel || !form.readingLevel || !form.speakingLevel || !form.writingLevel) {
            alert('All proficiency levels are required');
            return;
        }

        setIsSubmitting(true);
        try {
            const languageEntry = {
                language: form.language,
                listening: form.listeningLevel,
                listening_level: form.listeningLevel,
                listening_certified_since: form.listeningCertifiedSince,
                reading: form.readingLevel,
                reading_level: form.readingLevel,
                reading_certified_since: form.readingCertifiedSince,
                speaking: form.speakingLevel,
                speaking_level: form.speakingLevel,
                speaking_certified_since: form.speakingCertifiedSince,
                writing: form.writingLevel,
                writing_level: form.writingLevel,
                writing_certified_since: form.writingCertifiedSince,
                // Keep backwards compatibility with existing level field
                level: form.listeningLevel, // Use listening as primary level
            };

            await onSave(languageEntry);
            onClose();
        } catch (error) {
            console.error('Error saving language:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderProficiencyRow = (skill, levelKey, dateKey, label) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {label} <span className="text-red-500">*</span>
                </label>
                <select
                    value={form[levelKey]}
                    onChange={(e) => setForm({ ...form, [levelKey]: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                    required
                >
                    <option value="">Select</option>
                    {PROFICIENCY_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Certified/Held Since
                </label>
                <input
                    type="date"
                    value={form[dateKey]}
                    onChange={(e) => setForm({ ...form, [dateKey]: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                    placeholder="SELECT"
                />
            </div>
        </div>
    );

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title={isAddMode ? "Add Language" : "Edit Language"}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitLabel={isAddMode ? "Add" : "Update"}
            size="md"
        >
            <div className="space-y-5">
                {/* Language Selection */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                        Language <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={form.language}
                        onChange={(e) => setForm({ ...form, language: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                        required
                    >
                        <option value="">Select</option>
                        {LANGUAGES.map((lang) => (
                            <option key={lang} value={lang}>{lang}</option>
                        ))}
                    </select>
                </div>

                {/* Proficiency Levels */}
                {renderProficiencyRow('listening', 'listeningLevel', 'listeningCertifiedSince', 'Listening')}
                {renderProficiencyRow('reading', 'readingLevel', 'readingCertifiedSince', 'Reading')}
                {renderProficiencyRow('speaking', 'speakingLevel', 'speakingCertifiedSince', 'Speaking')}
                {renderProficiencyRow('writing', 'writingLevel', 'writingCertifiedSince', 'Writing')}
            </div>
        </EditModalWrapper>
    );
};

export default EditLanguageModal;
